import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { Collab, UserType } from "../../../../../../gql/__generated__/graphql";
import FormattedNumberInput from "../../../../../components/FormattedNumberInput/FormattedNumberInput";

interface CounterOfferModalProps {
  opened: boolean;
  onClose: () => void;
  collab: Collab;
  userRole: UserType;
  onSubmit: (newPrice: number, message?: string) => Promise<void>;
}

function CounterOfferModal(props: CounterOfferModalProps) {
  const [newPrice, setNewPrice] = useState<number | "">(props.collab.price);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const priceNumber =
      typeof newPrice === "number"
        ? newPrice
        : parseFloat(newPrice.toString().replace(/,/g, ""));

    if (isNaN(priceNumber) || priceNumber <= 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      await props.onSubmit(priceNumber, message.trim() || undefined);
      // On success, the parent component will close the modal, unmounting this component.
      // There's no need to reset state or call setIsSubmitting(false) on success,
      // as the component will be gone.
    } catch (error) {
      // If onSubmit fails, the modal remains open, so we should re-enable the form.
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNewPrice(props.collab.price);
      setMessage("");
      props.onClose();
    }
  };

  const currentPrice = props.collab.price;
  const proposedPrice =
    typeof newPrice === "number"
      ? newPrice
      : parseFloat(newPrice.toString().replace(/,/g, "")) || 0;
  const priceDifference = proposedPrice - currentPrice;
  const isValidPrice = proposedPrice > 0 && proposedPrice !== currentPrice;

  return (
    <Modal
      opened={props.opened}
      onClose={handleClose}
      title={<Title order={3}>Make Counter Offer</Title>}
      size="md"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Current offer:{" "}
          <Text span fw={600} c="secondary.6">
            ${currentPrice.toLocaleString()}
          </Text>
        </Text>

        <FormattedNumberInput
          label="Your Counter Offer"
          placeholder="Enter new price"
          value={newPrice}
          onChange={setNewPrice}
          allowDecimals={true}
          leftSection="$"
          required
          error={
            proposedPrice <= 0
              ? "Price must be greater than $0"
              : proposedPrice === currentPrice
              ? "New price must be different from current offer"
              : undefined
          }
        />

        {isValidPrice && (
          <Text size="sm" c={priceDifference > 0 ? "green" : "red"} fw={500}>
            {priceDifference > 0 ? "+" : ""}$
            {Math.abs(priceDifference).toLocaleString()}
            {priceDifference > 0 ? " increase" : " decrease"}
          </Text>
        )}

        <Textarea
          label="Message (Optional)"
          placeholder="Add a note to explain your counter offer..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          minRows={3}
          maxRows={5}
        />

        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!isValidPrice}
            color="primary"
          >
            Send Counter Offer
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default CounterOfferModal;
